import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import {
  PageContainer, PremiumCard, FormSection, Field, SelectField, Button,
} from "components/ui";

const AddPlan = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", type: "BASIC", description: "",
    monthlyPrice: "", yearlyPrice: "", currency: "TND",
    maxBranches: 1, maxUsersPerBranch: 5, maxProductsPerBranch: 100,
    active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Plan name is required.";
    if (!form.type) errs.type = "Plan type is required.";
    if (!form.description.trim()) errs.description = "Description is required.";
    if (!form.monthlyPrice || isNaN(form.monthlyPrice) || Number(form.monthlyPrice) < 0)
      errs.monthlyPrice = "Enter a valid monthly price.";
    if (!form.yearlyPrice || isNaN(form.yearlyPrice) || Number(form.yearlyPrice) < 0)
      errs.yearlyPrice = "Enter a valid yearly price.";
    if (!form.currency) errs.currency = "Currency is required.";
    if (!form.maxBranches || form.maxBranches < 1) errs.maxBranches = "Must be at least 1.";
    if (!form.maxUsersPerBranch || form.maxUsersPerBranch < 1) errs.maxUsersPerBranch = "Must be at least 1.";
    if (!form.maxProductsPerBranch || form.maxProductsPerBranch < 1) errs.maxProductsPerBranch = "Must be at least 1.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axiosPrivate.post("/api/plans", {
        name: form.name.trim(),
        type: form.type,
        description: form.description.trim(),
        monthlyPrice: parseFloat(form.monthlyPrice),
        yearlyPrice: parseFloat(form.yearlyPrice),
        currency: form.currency,
        maxBranches: parseInt(form.maxBranches),
        maxUsersPerBranch: parseInt(form.maxUsersPerBranch),
        maxProductsPerBranch: parseInt(form.maxProductsPerBranch),
        active: form.active,
      });
      toast.success("Plan created successfully!");
      navigate("/admin/list-plan");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer narrow>
      <PremiumCard title="Add new plan" subtitle="Define a subscription tier for your tenants">
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: 24 }}>
            <FormSection title="Identity" columns={2}>
              <Field label="Plan name" required value={form.name} onChange={set("name")} error={errors.name} placeholder="e.g. Starter, Pro, Enterprise" />
              <SelectField label="Plan type" required value={form.type} onChange={set("type")} error={errors.type}>
                <option value="BASIC">Basic</option>
                <option value="PRO">Pro</option>
                <option value="ENTERPRISE">Enterprise</option>
              </SelectField>
              <div style={{ gridColumn: "1 / -1" }}>
                <Field
                  label="Description" required as="textarea" rows={3}
                  value={form.description} onChange={set("description")}
                  error={errors.description}
                  placeholder="Briefly describe what this plan offers…"
                />
              </div>
            </FormSection>

            <FormSection title="Pricing" columns={3}>
              <SelectField label="Currency" value={form.currency} onChange={set("currency")} error={errors.currency}>
                <option value="TND">TND</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </SelectField>
              <Field label="Monthly price" required type="number" step="0.01" min="0" placeholder="0.00" value={form.monthlyPrice} onChange={set("monthlyPrice")} error={errors.monthlyPrice} />
              <Field label="Yearly price"  required type="number" step="0.01" min="0" placeholder="0.00" value={form.yearlyPrice}  onChange={set("yearlyPrice")}  error={errors.yearlyPrice} />
            </FormSection>

            <FormSection title="Limits" description="Per-tenant caps" columns={3}>
              <Field label="Max branches"        required type="number" min="1" value={form.maxBranches}          onChange={set("maxBranches")}          error={errors.maxBranches} />
              <Field label="Max users / branch"  required type="number" min="1" value={form.maxUsersPerBranch}    onChange={set("maxUsersPerBranch")}    error={errors.maxUsersPerBranch} />
              <Field label="Max products / branch" required type="number" min="1" value={form.maxProductsPerBranch} onChange={set("maxProductsPerBranch")} error={errors.maxProductsPerBranch} />
            </FormSection>

            <FormSection title="Availability">
              <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  style={{ accentColor: "var(--brand-primary)", width: 18, height: 18 }}
                />
                <span>Plan is immediately available for tenants</span>
              </label>
            </FormSection>
          </div>

          <div className="form-actionbar">
            <Button type="button" variant="secondary" onClick={() => navigate("/admin/list-plan")}>Cancel</Button>
            <Button type="submit" variant="primary" loading={loading}>Create plan</Button>
          </div>
        </form>
      </PremiumCard>
    </PageContainer>
  );
};

export default AddPlan;
